/* @flow */
import gql from 'graphql-tag';
import idx from 'idx';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';

import LoadingIndicator from 'components/LoadingIndicator.jsx';
import awaitData from 'lib/awaitData';
import AdminDistrictSisDashboard from 'admin/components/AdminDistrictSisDashboard.jsx';
import AdminSchoolSisDashboard from 'admin/components/AdminSchoolSisDashboard.jsx';
import AdminSisSyncPicker from 'admin/components/AdminSisSyncPicker.jsx';

type Organization = {
  id: number,
  uuid: string,
  type: string,
  permissions: Object,
  sisSyncOverview: {
    districtStatus: {
      awaitingFiles: boolean,
      schoolMappings: [Object],
      uploadErrors: [Object],
    },
  },
  childOrganizations: [
    {
      uuid: string,
      sisSyncOverview: {
        syncMethod: string,
      },
    },
  ],
};

type Props = {
  organization: Organization,
};

type State = {
  syncMethod: ?string,
};

export function getCurrentSyncMethodForOrg(org: Organization) {
  if (org.sisSyncOverview === null) {
    return null;
  }
  const { districtStatus } = org.sisSyncOverview;
  const cleverSynced = org.childOrganizations.some(
    childOrg => childOrg.sisSyncOverview.syncMethod === 'Clever'
  );

  const { awaitingFiles } = districtStatus;
  const hasSchoolMappings = districtStatus.schoolMappings.length > 0;
  const hasUploadErrors = districtStatus.uploadErrors.length > 0;
  const canSeeSftp = org.permissions.canSeeSftpRostering;

  if (cleverSynced || !canSeeSftp) {
    return 'Clever';
  } else if (awaitingFiles || hasSchoolMappings || hasUploadErrors) {
    return 'SFTP';
  } else {
    return null;
  }
}

class AdminSisDashboardContainer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      syncMethod: getCurrentSyncMethodForOrg(props.organization),
    };
  }

  // Needed to ensure rerender when moving from school sync view to district sync view
  // using the browser's back button =[
  componentDidUpdate(prevProps: Props) {
    const prevOrg = prevProps.organization;
    const { organization } = this.props;
    if (organization.id !== prevOrg.id) {
      this.setSyncMethod();
    }
  }

  setSyncMethod() {
    const { organization } = this.props;
    this.setState({ syncMethod: getCurrentSyncMethodForOrg(organization) });
  }

  handlePickSyncMethod = (syncMethod: string) => {
    this.setState({ syncMethod });
  };

  render() {
    const { organization } = this.props;
    const { syncMethod } = this.state;

    const canSeeDashboard = organization.permissions.adminConsole.seeSettingsTabSyncOverview;

    if (!canSeeDashboard) {
      console.error('SIS management page - non-paid accounts should not be here');
      // TODO: redirect to somewhere better.
      return <span>Uh, oh. You should not be here, something weird happened.</span>;
    }

    if (organization.type === 'District') {
      if (syncMethod === null) {
        return <AdminSisSyncPicker pickSyncMethod={this.handlePickSyncMethod} />;
      } else {
        return <AdminDistrictSisDashboard orgId={organization.id} syncMethod={syncMethod} />;
      }
    } else {
      return <AdminSchoolSisDashboard orgId={organization.id} />;
    }
  }
}

const withOrganization = graphql(
  gql`
    query AdminSisDashboard($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        id
        uuid
        type
        sisSyncOverview {
          districtStatus {
            awaitingFiles
            schoolMappings {
              schoolId
            }
            uploadErrors {
              error
            }
          }
        }
        childOrganizations {
          uuid
          sisSyncOverview {
            syncMethod
          }
        }
        permissions {
          canSeeSftpRostering
          adminConsole {
            seeSettingsTabSyncOverview
          }
        }
      }
    }
  `,
  {
    options: ({ orgId }) => ({
      variables: {
        organizationId: orgId,
      },
    }),
    props: ({ data }) => ({
      organization: idx(data, _ => _.organizations[0]),
    }),
  }
);

export { AdminSisDashboardContainer };
export default compose(
  withOrganization,
  awaitData(['organization'], <LoadingIndicator height={400} size="small" />)
)(AdminSisDashboardContainer);
